import { useEffect, useState } from 'react';
import { Banner as BannerData } from '@api/banner';
import { getAppSessionConfig, setAppSessionConfig } from '@utils/appConfigStorage';
import Banner from './Banner';
import { BannerDot, BannerDots, ActiveBannerDot } from './BannerStyles';

interface BannerCarouselProps {
  banners: BannerData[];
}

const getInitialClosedSortOrders = (): number[] => {
  const stored = getAppSessionConfig();
  return stored && Array.isArray(stored.bn_cl_so) ? stored.bn_cl_so : [];
};

const persistClosedSortOrders = (sortOrders: number[]): void => {
  setAppSessionConfig('bn_cl_so', sortOrders);
};

const BannerCarousel = ({ banners }: BannerCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [closedSortOrders, setClosedSortOrders] = useState<number[]>(getInitialClosedSortOrders);
  const [closingSortOrder, setClosingSortOrder] = useState<number | null>(null);

  const sortedBanners = [...banners]
    .filter((banner) => !closedSortOrders.includes(banner.sortOrder))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const activeIndex = Math.max(0, Math.min(currentIndex, sortedBanners.length - 1));

  useEffect(() => {
    if (currentIndex !== activeIndex) {
      setCurrentIndex(activeIndex);
    }
  }, [currentIndex, activeIndex]);

  useEffect(() => {
    if (sortedBanners.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((previousIndex) => (previousIndex + 1) % sortedBanners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [sortedBanners.length]);

  useEffect(() => {
    persistClosedSortOrders(closedSortOrders);
  }, [closedSortOrders]);

  if (sortedBanners.length === 0) {
    return null;
  }

  const handleClose = (sortOrder: number) => {
    setClosingSortOrder(sortOrder);

    window.setTimeout(() => {
      setClosedSortOrders((previous) => [...previous, sortOrder]);
      setClosingSortOrder(null);
    }, 400);
  };

  return (
    <>
      <Banner
        banner={sortedBanners[activeIndex]}
        isClosing={closingSortOrder === sortedBanners[activeIndex].sortOrder}
        onClose={() => handleClose(sortedBanners[activeIndex].sortOrder)}
      />

      {sortedBanners.length > 1 && (
        <BannerDots>
          {sortedBanners.map((banner, index) =>
            index === activeIndex ? (
              <ActiveBannerDot key={banner.sortOrder} />
            ) : (
              <BannerDot key={banner.sortOrder} />
            ),
          )}
        </BannerDots>
      )}
    </>
  );
};

export default BannerCarousel;
